import React, { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, MenuItem, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';
import PrivacyNote from '../components/PrivacyNote';
import StateBoundary from '../components/StateBoundary';
import StatusChip from '../components/StatusChip';
import { useSession } from '../viewmodels/SessionViewModel';
import { useConnectionsViewModel } from '../viewmodels/ConnectionsViewModel';

const PROVIDER_LABEL = { openai: 'OpenAI', anthropic: 'Anthropic (Claude)' };

function Current({ connection, label, detail, onRemove, disabled }) {
  if (!connection) return null;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      <StatusChip status={connection.status} />
      <Typography>
        {label}
        {' '}
        <Typography component="span" color="text.secondary">{detail}</Typography>
      </Typography>
      <Button color="error" size="small" disabled={disabled} onClick={onRemove}>Remove</Button>
    </Stack>
  );
}

export default function ConnectionsView() {
  const { isOwner } = useSession();
  const vm = useConnectionsViewModel();
  const [provider, setProvider] = useState('openai');
  const [apiKey, setApiKey] = useState('');
  const [analyticsKey, setAnalyticsKey] = useState('');
  const [whatsappKey, setWhatsappKey] = useState('');
  const [storeKey, setStoreKey] = useState('');
  const [storeId, setStoreId] = useState('');
  const [remove, setRemove] = useState(null);

  const saveAi = async (e) => {
    e.preventDefault();
    if (await vm.saveAiKey(provider, apiKey.trim())) setApiKey('');
  };
  const saveAnalytics = async (e) => {
    e.preventDefault();
    if (await vm.saveAnalyticsKey(analyticsKey.trim())) setAnalyticsKey('');
  };
  const saveWhatsapp = async (e) => {
    e.preventDefault();
    if (await vm.saveWhatsappKey(whatsappKey.trim())) setWhatsappKey('');
  };
  const saveOrka = async (e) => {
    e.preventDefault();
    if (await vm.saveStoreKey(storeKey.trim(), storeId || undefined)) {
      setStoreKey('');
      setStoreId('');
    }
  };

  return (
    <>
      <PageHeader title="Connections" subtitle="Connect the AI that powers your team, then the apps it can read: WhatsApp, analytics and, if you have one, your store." />
      {!isOwner && <Alert severity="info" sx={{ mb: 2.5 }}>Only the account owner can change connections.</Alert>}
      <StateBoundary loading={vm.loading} error={vm.error} onRetry={vm.reload}>
        <Stack spacing={2.5}>
          <Card>
            <CardContent component="form" onSubmit={saveAi}>
              <Typography variant="h6" gutterBottom>AI provider</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Your team uses your own AI key, so usage is billed by the provider to you. The key is stored encrypted and is never shown again.
              </Typography>
              <Current
                connection={vm.ai}
                label={PROVIDER_LABEL[vm.ai?.provider] || vm.ai?.provider}
                detail={vm.ai ? `key ending ${vm.ai.secret_last4}` : ''}
                disabled={!isOwner || Boolean(vm.busy)}
                onRemove={() => setRemove({ id: vm.ai.id, name: 'AI key' })}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField select label="Provider" value={provider} onChange={(e) => setProvider(e.target.value)} sx={{ minWidth: 200 }} disabled={!isOwner}>
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                </TextField>
                <TextField
                  label={vm.ai ? 'New API key (replaces current)' : 'API key'}
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  autoComplete="off"
                  fullWidth
                  disabled={!isOwner}
                />
                <Button type="submit" variant="contained" disabled={!isOwner || !apiKey.trim() || vm.busy === 'ai'} sx={{ minWidth: 120 }}>
                  {vm.busy === 'ai' ? <CircularProgress size={20} color="inherit" /> : 'Check and save'}
                </Button>
              </Stack>
              <PrivacyNote sx={{ mt: 2 }} />
            </CardContent>
          </Card>

          <Card>
            <CardContent component="form" onSubmit={saveOrka}>
              <Typography variant="h6" gutterBottom>Orka store (optional)</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Lets your team read recent orders and low stock from your Orka store. It cannot change anything.
                Create an API key in your Orka store settings, then paste it here.
              </Typography>
              <Current
                connection={vm.orka}
                label={vm.orka?.metadata?.store_name || 'Store'}
                detail={vm.orka ? `key ending ${vm.orka.secret_last4}` : ''}
                disabled={!isOwner || Boolean(vm.busy)}
                onRemove={() => setRemove({ id: vm.orka.id, name: 'store connection' })}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label={vm.orka ? 'New store API key (replaces current)' : 'Store API key'}
                  placeholder="orka_ak_..."
                  type="password"
                  value={storeKey}
                  onChange={(e) => setStoreKey(e.target.value)}
                  autoComplete="off"
                  fullWidth
                  disabled={!isOwner}
                />
                {vm.storeChoices && (
                  <TextField select label="Store" value={storeId} onChange={(e) => setStoreId(e.target.value)} sx={{ minWidth: 200 }} required>
                    {vm.storeChoices.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                  </TextField>
                )}
                <Button type="submit" variant="contained" disabled={!isOwner || !storeKey.trim() || vm.busy === 'orka' || (Boolean(vm.storeChoices) && !storeId)} sx={{ minWidth: 120 }}>
                  {vm.busy === 'orka' ? <CircularProgress size={20} color="inherit" /> : 'Check and save'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <Card>
            <CardContent component="form" onSubmit={saveAnalytics}>
              <Typography variant="h6" gutterBottom>Social media and ads (optional)</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Lets your Marketing Manager read how your social media and ads are doing (Meta, Google Ads, GA4 and more).
                It only reads numbers. Create an API key in Orka Analytics, then paste it here.
              </Typography>
              <Current
                connection={vm.analytics}
                label="Analytics"
                detail={vm.analytics ? `key ending ${vm.analytics.secret_last4}` : ''}
                disabled={!isOwner || Boolean(vm.busy)}
                onRemove={() => setRemove({ id: vm.analytics.id, name: 'analytics connection' })}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label={vm.analytics ? 'New analytics API key (replaces current)' : 'Analytics API key'}
                  type="password"
                  value={analyticsKey}
                  onChange={(e) => setAnalyticsKey(e.target.value)}
                  autoComplete="off"
                  fullWidth
                  disabled={!isOwner}
                />
                <Button type="submit" variant="contained" disabled={!isOwner || !analyticsKey.trim() || vm.busy === 'analytics'} sx={{ minWidth: 120 }}>
                  {vm.busy === 'analytics' ? <CircularProgress size={20} color="inherit" /> : 'Check and save'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <Card>
            <CardContent component="form" onSubmit={saveWhatsapp}>
              <Typography variant="h6" gutterBottom>WhatsApp (optional)</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Connect your WhatsApp account so your team can propose messages to customers. Connecting does not send anything:
                you control it in Settings, and every message needs your approval. Create an API key in WappGrow, then paste it here.
              </Typography>
              <Current
                connection={vm.whatsapp}
                label={vm.whatsapp?.label || 'WhatsApp'}
                detail={vm.whatsapp ? `key ending ${vm.whatsapp.secret_last4}` : ''}
                disabled={!isOwner || Boolean(vm.busy)}
                onRemove={() => setRemove({ id: vm.whatsapp.id, name: 'WhatsApp connection' })}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label={vm.whatsapp ? 'New WhatsApp API key (replaces current)' : 'WhatsApp API key'}
                  type="password"
                  value={whatsappKey}
                  onChange={(e) => setWhatsappKey(e.target.value)}
                  autoComplete="off"
                  fullWidth
                  disabled={!isOwner}
                />
                <Button type="submit" variant="contained" disabled={!isOwner || !whatsappKey.trim() || vm.busy === 'whatsapp'} sx={{ minWidth: 120 }}>
                  {vm.busy === 'whatsapp' ? <CircularProgress size={20} color="inherit" /> : 'Check and save'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </StateBoundary>

      <ConfirmDialog
        open={Boolean(remove)}
        title={`Remove ${remove?.name}?`}
        body={<Box>Your team will stop working until you connect it again.</Box>}
        confirmLabel="Remove"
        color="error"
        busy={Boolean(vm.busy)}
        onConfirm={async () => { await vm.remove(remove.id); setRemove(null); }}
        onClose={() => setRemove(null)}
      />
      <Snackbar open={Boolean(vm.feedback)} autoHideDuration={6000} onClose={vm.clearFeedback} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {vm.feedback ? <Alert severity={vm.feedback.severity} onClose={vm.clearFeedback}>{vm.feedback.text}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
